import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchBlogFeed } from '../api/blog-feed-api';
import type { BlogFeedItem } from '../types';

const PAGE_LIMIT = 10;
const PREFETCH_THRESHOLD = 3;

interface UseBlogFeedState {
  items: BlogFeedItem[];
  isLoading: boolean;
  isFetchingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  fetchMore: () => void;
  retry: () => void;
}

export function useBlogFeed(subCategory: string | null): UseBlogFeedState {
  const [items, setItems] = useState<BlogFeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Prevent concurrent fetches
  const isFetchingRef = useRef(false);
  // Track which subCategory the current data belongs to
  const subCategoryRef = useRef(subCategory);

  const fetchInitial = useCallback(async (category: string | null) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);
    setItems([]);
    setNextCursor(null);
    setHasMore(false);

    try {
      const response = await fetchBlogFeed({
        subCategory: category,
        limit: PAGE_LIMIT,
      });

      // Only apply if the subCategory hasn't changed while we were fetching
      if (subCategoryRef.current === category) {
        setItems(response.items);
        setNextCursor(response.nextCursor);
        setHasMore(response.hasMore);
      }
    } catch (err) {
      if (subCategoryRef.current === category) {
        setError(err instanceof Error ? err : new Error('Failed to load blog feed'));
      }
    } finally {
      if (subCategoryRef.current === category) {
        setIsLoading(false);
      }
      isFetchingRef.current = false;
    }
  }, []);

  // Reset and re-fetch when subCategory changes
  useEffect(() => {
    subCategoryRef.current = subCategory;
    fetchInitial(subCategory);
  }, [subCategory, fetchInitial]);

  const fetchMore = useCallback(async () => {
    if (isFetchingRef.current || !hasMore || !nextCursor) return;
    isFetchingRef.current = true;
    setIsFetchingMore(true);
    setError(null);

    const currentCategory = subCategoryRef.current;

    try {
      const response = await fetchBlogFeed({
        subCategory: currentCategory,
        limit: PAGE_LIMIT,
        cursor: nextCursor,
      });

      if (subCategoryRef.current === currentCategory) {
        setItems(prev => [...prev, ...response.items]);
        setNextCursor(response.nextCursor);
        setHasMore(response.hasMore);
      }
    } catch (err) {
      if (subCategoryRef.current === currentCategory) {
        setError(err instanceof Error ? err : new Error('Failed to load more articles'));
      }
    } finally {
      setIsFetchingMore(false);
      isFetchingRef.current = false;
    }
  }, [hasMore, nextCursor]);

  const retry = useCallback(() => {
    fetchInitial(subCategoryRef.current);
  }, [fetchInitial]);

  return {
    items,
    isLoading,
    isFetchingMore,
    error,
    hasMore,
    fetchMore,
    retry,
  };
}

// Hook for triggering prefetch when user is within PREFETCH_THRESHOLD items of the end
export function usePrefetchTrigger(
  currentIndex: number,
  totalItems: number,
  fetchMore: () => void,
  hasMore: boolean,
  isFetchingMore: boolean
): void {
  const fetchMoreRef = useRef(fetchMore);
  fetchMoreRef.current = fetchMore;

  useEffect(() => {
    const itemsFromEnd = totalItems - 1 - currentIndex;
    if (hasMore && !isFetchingMore && itemsFromEnd <= PREFETCH_THRESHOLD) {
      fetchMoreRef.current();
    }
  }, [currentIndex, totalItems, hasMore, isFetchingMore]);
}
